/**
 * Currency utilities based on country codes
 */

// Currency mapping by country code
const CURRENCY_MAP = {
  // North America
  'US': { symbol: '$', code: 'USD', name: 'US Dollar' },
  'CA': { symbol: 'C$', code: 'CAD', name: 'Canadian Dollar' },
  'MX': { symbol: 'MX$', code: 'MXN', name: 'Mexican Peso' },
  
  // Europe
  'GB': { symbol: '£', code: 'GBP', name: 'British Pound' },
  'EU': { symbol: '€', code: 'EUR', name: 'Euro' },
  'DE': { symbol: '€', code: 'EUR', name: 'Euro' },
  'FR': { symbol: '€', code: 'EUR', name: 'Euro' },
  'IT': { symbol: '€', code: 'EUR', name: 'Euro' },
  'ES': { symbol: '€', code: 'EUR', name: 'Euro' },
  'NL': { symbol: '€', code: 'EUR', name: 'Euro' },
  'BE': { symbol: '€', code: 'EUR', name: 'Euro' },
  'AT': { symbol: '€', code: 'EUR', name: 'Euro' },
  'FI': { symbol: '€', code: 'EUR', name: 'Euro' },
  'IE': { symbol: '€', code: 'EUR', name: 'Euro' },
  'PT': { symbol: '€', code: 'EUR', name: 'Euro' },
  'GR': { symbol: '€', code: 'EUR', name: 'Euro' },
  'CH': { symbol: 'CHF', code: 'CHF', name: 'Swiss Franc' },
  'SE': { symbol: 'kr', code: 'SEK', name: 'Swedish Krona' },
  'NO': { symbol: 'kr', code: 'NOK', name: 'Norwegian Krone' },
  'DK': { symbol: 'kr', code: 'DKK', name: 'Danish Krone' },
  'PL': { symbol: 'zł', code: 'PLN', name: 'Polish Zloty' },
  'RU': { symbol: '₽', code: 'RUB', name: 'Russian Ruble' },
  'TR': { symbol: '₺', code: 'TRY', name: 'Turkish Lira' },
  
  // Asia Pacific
  'AU': { symbol: 'A$', code: 'AUD', name: 'Australian Dollar' },
  'NZ': { symbol: 'NZ$', code: 'NZD', name: 'New Zealand Dollar' },
  'JP': { symbol: '¥', code: 'JPY', name: 'Japanese Yen' },
  'CN': { symbol: '¥', code: 'CNY', name: 'Chinese Yuan' },
  'IN': { symbol: '₹', code: 'INR', name: 'Indian Rupee' },
  'SG': { symbol: 'S$', code: 'SGD', name: 'Singapore Dollar' },
  'MY': { symbol: 'RM', code: 'MYR', name: 'Malaysian Ringgit' },
  'TH': { symbol: '฿', code: 'THB', name: 'Thai Baht' },
  'PH': { symbol: '₱', code: 'PHP', name: 'Philippine Peso' },
  'ID': { symbol: 'Rp', code: 'IDR', name: 'Indonesian Rupiah' },
  'KR': { symbol: '₩', code: 'KRW', name: 'South Korean Won' },
  'VN': { symbol: '₫', code: 'VND', name: 'Vietnamese Dong' },
  
  // Middle East
  'AE': { symbol: 'د.إ', code: 'AED', name: 'UAE Dirham' },
  'SA': { symbol: '﷼', code: 'SAR', name: 'Saudi Riyal' },
  'IL': { symbol: '₪', code: 'ILS', name: 'Israeli Shekel' },
  'EG': { symbol: 'E£', code: 'EGP', name: 'Egyptian Pound' },
  
  // South America
  'BR': { symbol: 'R$', code: 'BRL', name: 'Brazilian Real' },
  'AR': { symbol: '$', code: 'ARS', name: 'Argentine Peso' },
  'CL': { symbol: '$', code: 'CLP', name: 'Chilean Peso' },
  'CO': { symbol: '$', code: 'COP', name: 'Colombian Peso' },
  'PE': { symbol: 'S/', code: 'PEN', name: 'Peruvian Sol' },
  
  // Africa
  'ZA': { symbol: 'R', code: 'ZAR', name: 'South African Rand' },
  
  // Other
  'PK': { symbol: '₨', code: 'PKR', name: 'Pakistani Rupee' },
  'BD': { symbol: '৳', code: 'BDT', name: 'Bangladeshi Taka' },
};

/**
 * Get currency information for a country code
 * @param {string} countryCode - ISO country code (e.g., 'US', 'GB', 'FR')
 * @returns {Object} Currency object with symbol, code, and name
 */
export const getCurrency = (countryCode) => {
  if (!countryCode) {
    return CURRENCY_MAP['US']; // Default to USD
  }
  
  const currency = CURRENCY_MAP[countryCode.toUpperCase()];
  return currency || CURRENCY_MAP['US']; // Fallback to USD
};

/**
 * Format a number as currency based on country
 * @param {number} amount - Amount to format
 * @param {string} countryCode - ISO country code
 * @param {Object} options - Formatting options
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount, countryCode, options = {}) => {
  const countryCurrency = getCurrency(countryCode);
  const { 
    showSymbol = true, 
    showCode = false,
    decimals = 0,
    useLocaleString = true,
    currency: explicitCurrencyCode,
    currencyDisplay = showCode ? 'code' : 'narrowSymbol'
  } = options;
  const resolvedCurrencyCode = (explicitCurrencyCode || countryCurrency.code || 'USD').toUpperCase();
  
  let formatted;
  if (useLocaleString && typeof amount === 'number') {
    const locale = getLocaleFromCountry(countryCode);
    const formatOptions = {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    };

    if (showCode || showSymbol) {
      formatOptions.style = 'currency';
      formatOptions.currency = resolvedCurrencyCode;
      formatOptions.currencyDisplay = currencyDisplay;
    } else {
      formatOptions.style = 'decimal';
    }

    formatted = new Intl.NumberFormat(locale, formatOptions).format(amount);
  } else {
    // Simple formatting
    formatted = amount.toLocaleString('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
    if (showCode || showSymbol) {
      if (showCode) {
        return `${resolvedCurrencyCode} ${formatted}`;
      }
      return `${countryCurrency.symbol}${formatted}`;
    }
  }
  
  return formatted;
};

/**
 * Get locale string from country code
 * @param {string} countryCode - ISO country code
 * @returns {string} Locale string (e.g., 'en-US', 'en-GB', 'fr-FR')
 */
const getLocaleFromCountry = (countryCode) => {
  const localeMap = {
    'US': 'en-US',
    'CA': 'en-CA',
    'GB': 'en-GB',
    'AU': 'en-AU',
    'NZ': 'en-NZ',
    'DE': 'de-DE',
    'FR': 'fr-FR',
    'IT': 'it-IT',
    'ES': 'es-ES',
    'NL': 'nl-NL',
    'BE': 'nl-BE',
    'AT': 'de-AT',
    'FI': 'fi-FI',
    'IE': 'en-IE',
    'PT': 'pt-PT',
    'GR': 'el-GR',
    'CH': 'de-CH',
    'SE': 'sv-SE',
    'NO': 'nb-NO',
    'DK': 'da-DK',
    'PL': 'pl-PL',
    'RU': 'ru-RU',
    'TR': 'tr-TR',
    'JP': 'ja-JP',
    'CN': 'zh-CN',
    'IN': 'en-IN',
    'SG': 'en-SG',
    'MY': 'ms-MY',
    'TH': 'th-TH',
    'PH': 'en-PH',
    'ID': 'id-ID',
    'KR': 'ko-KR',
    'VN': 'vi-VN',
    'BR': 'pt-BR',
    'AR': 'es-AR',
    'MX': 'es-MX',
    'CL': 'es-CL',
    'CO': 'es-CO',
    'PE': 'es-PE',
    'ZA': 'en-ZA',
    'AE': 'ar-AE',
    'SA': 'ar-SA',
    'IL': 'he-IL',
    'EG': 'ar-EG',
  };
  
  return localeMap[countryCode] || 'en-US';
};

/**
 * Get currency symbol for a country
 * @param {string} countryCode - ISO country code
 * @returns {string} Currency symbol
 */
export const getCurrencySymbol = (countryCode) => {
  return getCurrency(countryCode).symbol;
};

/**
 * Get currency code for a country
 * @param {string} countryCode - ISO country code
 * @returns {string} Currency code (e.g., 'USD', 'EUR', 'GBP')
 */
export const getCurrencyCode = (countryCode) => {
  return getCurrency(countryCode).code;
};


